
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Clock, Calendar, Save, User } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useTimetableActions } from '../hooks/useTimetableActions';
import { useTeacherData } from '../hooks/useTeacherData';
import { ClassItem } from '@/types/timetable';

const TimetableForm = () => {
  const navigate = useNavigate();
  const { teacherId, classId } = useParams();
  const { teachers } = useTeacherData();
  const { createClass, updateClass } = useTimetableActions();
  
  const [formData, setFormData] = useState({
    teacherId: teacherId || 'unassigned',
    subject: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    room: '',
    notes: '',
    status: 'present' as 'present' | 'late' | 'absent'
  });

  const [selectedTeacherName, setSelectedTeacherName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (teachers && teachers.length > 0 && formData.teacherId && formData.teacherId !== 'unassigned') {
      const teacher = teachers.find(t => t.id === formData.teacherId);
      if (teacher) {
        setSelectedTeacherName(`${teacher.firstName} ${teacher.lastName}`);
      }
    } else if (formData.teacherId === 'unassigned') {
      setSelectedTeacherName('Unassigned');
    }
  }, [teachers, formData.teacherId]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`).toISOString();
      const endDateTime = new Date(`${formData.date}T${formData.endTime}`).toISOString();
      
      const classData: Partial<ClassItem> = {
        subject: formData.subject,
        startTime: startDateTime,
        endTime: endDateTime,
        room: formData.room,
        status: formData.status, // Make sure status is properly included
        notes: formData.notes,
        teacherName: selectedTeacherName,
        teacherId: formData.teacherId
      };
      
      console.log('Submitting class data with status:', formData.status, classData);
      
      if (classId) {
        await updateClass(formData.teacherId, classId, classData);
        toast.success("Class updated successfully");
      } else {
        await createClass(formData.teacherId, classData);
        toast.success("Class created successfully");
      }
      
      navigate('/timetable');
      
    } catch (error) {
      console.error('Failed to save class:', error);
      toast.error("Failed to save class schedule");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="container p-4 mx-auto">
      <div className="flex items-center mb-6">
        <Button variant="ghost" size="sm" className="mr-2" onClick={() => navigate('/timetable')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">
          {classId ? 'Edit Class Schedule' : 'New Class Schedule'}
        </h1>
      </div>
      
      <Card className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle className="flex items-center">
              {classId ? 'Edit Class Details' : 'Schedule a New Class'}
              {selectedTeacherName && (
                <span className="ml-2 text-sm text-muted-foreground">
                  for {selectedTeacherName}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teacherId" className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                Teacher (Optional)
              </Label>
              <Select 
                value={formData.teacherId} 
                onValueChange={(value) => handleSelectChange('teacherId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a teacher or leave unassigned" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teachers && teachers.map(teacher => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.firstName} {teacher.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Date
                </Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="room">Room Number</Label>
                <Input
                  id="room"
                  name="room"
                  value={formData.room}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime" className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Start Time
                </Label>
                <Input
                  id="startTime"
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="endTime" className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  End Time
                </Label>
                <Input
                  id="endTime"
                  name="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Attendance Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value: 'present' | 'late' | 'absent') => {
                  console.log('Status changed to:', value);
                  handleSelectChange('status', value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select attendance status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present (On Time)</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Current status: <span className="font-medium capitalize">{formData.status}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any additional information"
                className="min-h-[100px]"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-wrap gap-2 justify-between">
            <Button type="button" variant="outline" onClick={() => navigate('/timetable')}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="whitespace-nowrap">
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Schedule'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default TimetableForm;
